import Done from '@mui/icons-material/Done'
import ErrorIcon from '@mui/icons-material/Error'
import {CircularProgress, IconButton} from '@mui/material'
import type {ButtonProps} from '@mui/material/Button/Button'
import {memo, useEffect, useMemo, useRef, useState} from 'react'
import type {Timer} from '../../@types/timeout'
import {AsyncStateError, AsyncStateInit, AsyncStateLoading, AsyncStateSuccess} from '../../enums/asyncState'
import {useAsyncState} from '../../states/AsyncContext'

interface Props extends ButtonProps {
  successTtl?: number
  errorTtl?: number
}

export const AsyncIconButton = memo(({
  successTtl = 2_000,
  errorTtl = 5_000,
  children,
  ...props
}: Props) => {
  const state = useAsyncState()
  const [displayState, setDisplayState] = useState(true)
  const timer = useRef<Timer>(undefined)

  useEffect(() => {
    setDisplayState(true)
    clearTimeout(timer.current)
    if ([AsyncStateLoading, AsyncStateInit].includes(state)) {
      return
    }
    timer.current = setTimeout(() => {
      setDisplayState(false)
    }, state === AsyncStateError ? errorTtl : successTtl)

    return () => {
      clearTimeout(timer.current)
    }
  }, [errorTtl, state, successTtl])

  const color = useMemo(() => {
    if (state === AsyncStateLoading) {
      return props.color ?? 'primary'
    }
    if (!displayState) {
      return props.color ?? 'primary'
    }
    if (state === AsyncStateError && errorTtl > 0) {
      return 'error'
    }
    if (state === AsyncStateSuccess && successTtl > 0) {
      return 'success'
    }

    return props.color ?? 'primary'
  }, [displayState, errorTtl, props.color, state, successTtl])

  const loadingIcon = useMemo(() => {
    if (state === AsyncStateLoading) {
      return <CircularProgress size={props.size === 'small' ? 18 : 24}/>
    }
    if (!displayState) {
      return undefined
    }
    if (state === AsyncStateError && errorTtl > 0) {
      return <ErrorIcon fontSize="inherit"/>
    }
    if (state === AsyncStateSuccess && successTtl > 0) {
      return <Done fontSize="inherit"/>
    }

    return undefined
  }, [displayState, errorTtl, props.size, state, successTtl])

  return <IconButton
    {...props}
    loading={undefined}
    disabled={state === AsyncStateLoading || props.disabled}
    type={props.type ?? 'submit'}
    color={color}
  >
    {loadingIcon ?? children}
  </IconButton>
})
